/**
 * Campaign report-figure images (S3-backed, referenced by object key on `CampaignReportFigure`).
 *
 * A report figure is either a chart (data, no bytes) or an uploaded image. Chart figures are plain
 * `CampaignReportFigure` rows written through the generic CRUD API. Image figures ALSO need bytes in
 * storage, uploaded the same three-step way journey-map attachments are, so the bytes never pass
 * through the API and a client cannot misreport an object's size or type:
 *
 *   1. POST `db/campaign-report-figure/presign/`            -> short-lived signed PUT + object key
 *   2. PUT the (shrunk) image straight at storage
 *   3. write the CampaignReportFigure row (generic CRUD) with the returned `object_key`
 *
 * Unlike journey-map files, step 3 is NOT a bespoke create endpoint here — the figure row is written
 * by the admin canvas through `useCampaignMutations('reportFigure')`, so `uploadReportFigureImage`
 * stops after the PUT and returns the object key + dimensions for the caller to store on the row.
 *
 * Reads are signed GET URLs fetched per use (`db/campaign-report-figure/{id}/download-url/`). They
 * expire, so they are never held in state; the report render resolves them on demand.
 *
 * The presign key prefix is `campaign-report-figures/<campaign>/…` (mirrors the journey-map package;
 * only the prefix differs). This feature is inert until the bucket is set AND CORS + the node-role
 * grant are in place per environment (the API answers 503 while the bucket is unset).
 */
import { buildApiUrl, getApiHeaders } from './queriesHelpers';
import { UploadProgressCallback, putFileToStorageWithProgress } from './xhrUpload';
import { prepareReportImage } from '../components/Campaigns/guided/modules/report/reportImages';

export type CampaignReportFigurePresign = {
  upload_url: string;
  object_key: string;
  content_type: string;
  file_name: string;
  expires_in: number;
  max_bytes: number;
};

export type CampaignReportFigureDownload = {
  url: string;
  expires_in: number;
  refresh_after: number;
  signed: boolean;
  file_name: string;
  content_type: string;
  size_bytes: number;
};

/** What `uploadReportFigureImage` hands back for the caller to store on the figure row. */
export type UploadedReportFigureImage = {
  object_key: string;
  content_type: string;
  width: number;
  height: number;
  size_bytes: number;
};

/** The S3 bucket is unset, so the whole image-figure feature is inert (API answers 503). */
export class CampaignReportFigureStorageUnavailableError extends Error {}

const PRESIGN_ENDPOINT = 'db/campaign-report-figure/presign/';
const READ_URL_ENDPOINT = 'db/campaign-report-figure/read-url/';
const downloadEndpoint = (figureId: number | string) =>
  `db/campaign-report-figure/${figureId}/download-url/`;

/** Presign and download bodies normalize errors to `detail`; field errors still come back keyed. */
const errorMessage = (payload: any, status: number, fallback: string): string => {
  const detail = payload?.detail ?? payload?.object_key ?? payload?.file_name ?? payload?.campaign;
  if (Array.isArray(detail) && detail.length) return String(detail[0]);
  if (detail) return String(detail);
  if (typeof payload === 'string' && payload.trim()) return payload;
  return `${fallback} (${status})`;
};

async function figureApiFetch<T = any>(
  method: 'GET' | 'POST',
  endpoint: string,
  options: { params?: Record<string, any>; body?: any } = {},
): Promise<T> {
  const { params = {}, body } = options;
  const headers = { ...getApiHeaders(null, method, endpoint) } as Record<string, string>;

  const response = await fetch(buildApiUrl(endpoint, params, true), {
    method,
    headers,
    credentials: 'include',
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

  const text = await response.text();
  let parsed: any = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    parsed = text;
  }

  if (!response.ok) {
    const message = errorMessage(parsed, response.status, 'Report figure image request failed');
    if (response.status === 503) throw new CampaignReportFigureStorageUnavailableError(message);
    const error: any = new Error(message);
    error.status = response.status;
    error.payload = parsed;
    throw error;
  }

  return parsed as T;
}

export async function presignReportFigureUpload(
  campaignId: number | string,
  file: { name: string; type?: string },
): Promise<CampaignReportFigurePresign> {
  return await figureApiFetch<CampaignReportFigurePresign>('POST', PRESIGN_ENDPOINT, {
    body: {
      campaign: campaignId,
      file_name: file.name,
      content_type: file.type || 'application/octet-stream',
    },
  });
}

/** A shrunk data URL back into a File for upload, keeping the encoder's chosen type. */
async function dataUrlToFile(dataUrl: string, name: string): Promise<File> {
  const res = await fetch(dataUrl);
  const blob = await res.blob();
  const type = blob.type || (dataUrl.startsWith('data:image/jpeg') ? 'image/jpeg' : 'image/png');
  const ext = type === 'image/jpeg' ? 'jpg' : type.split('/')[1] || 'png';
  const base = name.replace(/\.[^.]+$/, '') || 'figure';
  return new File([blob], `${base}.${ext}`, { type });
}

/**
 * Shrink an image the SE picked and upload the bytes to storage, returning the object key and
 * dimensions for the caller to store on a `CampaignReportFigure` row.
 *
 * The bytes are shrunk with the same `prepareReportImage` the SE-owned workshop images use (bounded
 * longest edge, smaller of PNG/JPEG), so an admin figure image is the same size class as a workshop
 * one; unlike the workshop path the bytes go to S3, not into enrollment JSON. Throws on a file that
 * isn't a supported image, that's over the shrink limit, or when storage is unavailable.
 */
export async function uploadReportFigureImage(
  campaignId: number | string,
  file: File,
  options: { onProgress?: UploadProgressCallback } = {},
): Promise<UploadedReportFigureImage> {
  const prepared = await prepareReportImage(file);
  const upload = await dataUrlToFile(prepared.dataUrl, file.name);

  const presign = await presignReportFigureUpload(campaignId, upload);
  if (presign.max_bytes && upload.size > presign.max_bytes) {
    throw new Error(
      `That picture is larger than the ${Math.round(presign.max_bytes / (1024 * 1024))} MB limit.`,
    );
  }

  await putFileToStorageWithProgress(
    presign.upload_url,
    upload,
    presign.content_type || upload.type || 'application/octet-stream',
    options.onProgress,
  );

  return {
    object_key: presign.object_key,
    content_type: presign.content_type || upload.type || 'image/png',
    width: prepared.width,
    height: prepared.height,
    size_bytes: upload.size,
  };
}

export async function getReportFigureDownload(
  figureId: number | string,
): Promise<CampaignReportFigureDownload> {
  return await figureApiFetch<CampaignReportFigureDownload>('GET', downloadEndpoint(figureId));
}

/**
 * Upload an SE-added report image's bytes to storage, keyed off the campaign AND this delivery so
 * one delivery's picture can never collide with another's (or another campaign's).
 *
 * Unlike {@link uploadReportFigureImage} (the admin canvas figures, keyed by campaign only, whose
 * object key lands on a `CampaignReportFigure` row), an SE image has no figure row — its object key
 * is stored in the delivery's enrollment metadata. The per-delivery namespace comes from the same
 * presign endpoint by passing `item="delivery-<deliveryId>"` and `figure_id="<imageId>"`, which the
 * key builder appends below the campaign segment: `…/<campaign>/delivery-<id>/<imageId>/<uuid>.ext`.
 * Returns the object key + type + dimensions for the caller to store on the `ReportImage`. Throws on
 * an unsupported/oversized image or when storage is unavailable.
 */
export async function uploadDeliveryReportImage(
  campaignId: number | string,
  deliveryId: number | string,
  imageId: string,
  file: File,
  options: { onProgress?: UploadProgressCallback } = {},
): Promise<UploadedReportFigureImage> {
  const prepared = await prepareReportImage(file);
  const upload = await dataUrlToFile(prepared.dataUrl, file.name);

  const presign = await figureApiFetch<CampaignReportFigurePresign>('POST', PRESIGN_ENDPOINT, {
    body: {
      campaign: campaignId,
      item: `delivery-${deliveryId}`,
      figure_id: imageId,
      file_name: upload.name,
      content_type: upload.type || 'application/octet-stream',
    },
  });
  if (presign.max_bytes && upload.size > presign.max_bytes) {
    throw new Error(
      `That picture is larger than the ${Math.round(presign.max_bytes / (1024 * 1024))} MB limit.`,
    );
  }

  await putFileToStorageWithProgress(
    presign.upload_url,
    upload,
    presign.content_type || upload.type || 'application/octet-stream',
    options.onProgress,
  );

  return {
    object_key: presign.object_key,
    content_type: presign.content_type || upload.type || 'image/png',
    width: prepared.width,
    height: prepared.height,
    size_bytes: upload.size,
  };
}

/**
 * Fetch an SE-added image's bytes as a data URL from its stored object key (no figure row to sign
 * by — the read-by-key endpoint signs the GET, gated to the campaign the key belongs to). Used both
 * as the on-screen `<img src>` and embedded into the Word/PDF export. Best-effort: returns '' on any
 * failure so a report never breaks over one picture. Needs bucket CORS for the app origin to succeed
 * cross-origin, same as {@link fetchReportFigureDataUrl}.
 */
export async function fetchDeliveryReportImageUrl(
  campaignId: number | string,
  objectKey: string,
): Promise<string> {
  if (!objectKey) return '';
  try {
    const { url } = await figureApiFetch<CampaignReportFigureDownload>('POST', READ_URL_ENDPOINT, {
      body: { campaign: campaignId, object_key: objectKey },
    });
    if (!url) return '';
    const res = await fetch(url, { credentials: 'omit' });
    if (!res.ok) return '';
    const blob = await res.blob();
    return await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(typeof reader.result === 'string' ? reader.result : '');
      reader.onerror = () => resolve('');
      reader.readAsDataURL(blob);
    });
  } catch {
    return '';
  }
}

/**
 * Fetch an image figure's bytes as a data URL, for embedding in the Word/PDF export (an `<img src>`
 * on screen can use the signed URL directly and needs no fetch). Best-effort: returns '' on any
 * failure so an export never breaks over one picture. Needs bucket CORS for the app origin, or the
 * same-origin byte-proxy fallback, to succeed cross-origin.
 */
export async function fetchReportFigureDataUrl(figureId: number | string): Promise<string> {
  try {
    const { url } = await getReportFigureDownload(figureId);
    if (!url) return '';
    const res = await fetch(url, { credentials: 'omit' });
    if (!res.ok) return '';
    const blob = await res.blob();
    return await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(typeof reader.result === 'string' ? reader.result : '');
      reader.onerror = () => resolve('');
      reader.readAsDataURL(blob);
    });
  } catch {
    return '';
  }
}
